import moment from "moment";

import { COVER_CROP_MIX_TYPE, CoverCropMixType, SCENARIO_LIMITS, SOIL_SIGHT_TILLAGE, SoilSightTillage } from "consts";

import {
  CROP_TYPE_FCPO,
  type CropFCPO,
  type CropRotationAnalysis,
  type CropRotationResult,
  type CropRotationStep,
  PRACTICE_SLUG,
  type PracticeFCPO,
  ZERO_UUID,
} from "models";

import { getCropYearFromDates } from "pages/app/all-field-view/utils/AllFieldsUtils";
import { CropYear, ScenarioMainCropOption } from "pages/app/scenario-analysis/types/scenario";
import { padCropYearsToMinimum } from "pages/app/scenario-analysis/utils/padSoilSightCropYears";

/** When options are loaded, only Silver-model-ready main crops are accepted; otherwise pass crop id through. */
const resolveSoilSightMainCropId = (cropId: string | undefined, mainCropOptions: ScenarioMainCropOption[]): string => {
  if (!cropId?.trim()) return "";
  if (mainCropOptions.length === 0) return cropId;
  return mainCropOptions.some((o) => o.value === cropId) ? cropId : "";
};

const VALID_COVER_CROP_IDS: ReadonlySet<CoverCropMixType> = new Set(Object.values(COVER_CROP_MIX_TYPE));

const isCoverCropMixType = (cropId: string): cropId is CoverCropMixType => VALID_COVER_CROP_IDS.has(cropId as CoverCropMixType);

const getCoverCropId = (cropId: string): CoverCropMixType => (isCoverCropMixType(cropId) ? cropId : COVER_CROP_MIX_TYPE.SUMMER_SINGLE_SPECIES);

type FieldCropYearEntry = {
  year: number;
  mainCrop: CropFCPO;
  coverCrop?: CropFCPO;
};

/**
 * Maps a field's crop rotation entries to SoilSight crop years for the Baseline Scenario.
 *
 * Rules (per KH-182):
 * - Start at the oldest year that has a main crop; take up to MAX_YEARS (10), pad up to MIN_YEARS (4).
 * - Main crop: cropId kept only when it appears in `mainCropOptions` (Silver-model main crops); empty if unknown.
 * - Residue: "incorporated" when hasResidue is true, "removed" otherwise.
 * - Cover crop: mapped to a mix type by crop ID (4 known cover crop IDs); "none" when absent.
 * - Tillage: "conventional" when the crop has a tillage practice with a real assignedPracticeId, "no-till" otherwise.
 */
/** Used by mapFieldCropsToSoilSightYears — always returns a value (falls back to no-till UUID). */
const getTillageId = (cropId: string, practices: PracticeFCPO[]): SoilSightTillage => {
  const tillagePractice = practices.find((p) => p.type === PRACTICE_SLUG.TILLAGES && p.cropId === cropId);
  const hasRealPractice = tillagePractice && tillagePractice.assignedPracticeId && tillagePractice.assignedPracticeId !== ZERO_UUID;
  return hasRealPractice ? SOIL_SIGHT_TILLAGE.CONVENTIONAL : SOIL_SIGHT_TILLAGE.NONE;
};

/**
 * Used by mapCropRotationToSoilSightYears — returns null when no real tillage practice is found
 * so the UI input stays unselected rather than defaulting to no-till.
 */
const getTillageIdOrNull = (cropId: string, practices: PracticeFCPO[]): SoilSightTillage | null => {
  const tillagePractice = practices.find((p) => p.type === PRACTICE_SLUG.TILLAGES && p.cropId === cropId);
  const hasRealPractice = tillagePractice && tillagePractice.assignedPracticeId && tillagePractice.assignedPracticeId !== ZERO_UUID;
  return hasRealPractice ? SOIL_SIGHT_TILLAGE.CONVENTIONAL : null;
};

const getFieldCropYearEntries = (fieldCrops: CropFCPO[]): FieldCropYearEntry[] => {
  const byYear = new Map<number, { mainCrop?: CropFCPO; coverCrop?: CropFCPO }>();

  fieldCrops.forEach((crop) => {
    const year = getCropYearFromDates(
      crop.cropType,
      moment(crop.seedingStartDate ?? crop.fallbackDates?.seedingStartDate ?? null),
      moment(crop.harvestEndDate ?? crop.fallbackDates?.harvestEndDate ?? null)
    );
    if (year == null) return;
    if (!byYear.has(year)) byYear.set(year, {});
    const entry = byYear.get(year)!;
    if (crop.cropType === CROP_TYPE_FCPO.MAIN && !entry.mainCrop) entry.mainCrop = crop;
    else if (crop.cropType === CROP_TYPE_FCPO.COVER && !entry.coverCrop) entry.coverCrop = crop;
  });

  const yearsWithMain = Array.from(byYear.entries())
    .filter(([, e]) => e.mainCrop)
    .sort(([a], [b]) => a - b);

  return yearsWithMain.map(([year, entry]) => {
    const { mainCrop, coverCrop } = entry as { mainCrop: CropFCPO; coverCrop?: CropFCPO };
    return { year, mainCrop, coverCrop };
  });
};

const getActiveRotationAnalysis = (rotation: CropRotationResult): CropRotationAnalysis => {
  if (rotation.setAside && rotation.rotationBeforeSetAside?.pattern.length) return rotation.rotationBeforeSetAside;
  return rotation;
};

const getExpandedStepCount = (stepCount: number): number => {
  if (stepCount <= 0) return 0;

  let count = stepCount;
  while (count < SCENARIO_LIMITS.MIN_YEARS) count += stepCount;

  return Math.min(count, SCENARIO_LIMITS.MAX_YEARS);
};

const uniqueStrings = (values: Array<string | null | undefined>): string[] => [...new Set(values.filter((value): value is string => Boolean(value?.trim())))];

const getRotationStepCropIdCandidates = (step: CropRotationStep): string[] =>
  uniqueStrings([...(step.cropIdOptions ?? []).map((option) => option.cropId), step.exampleCropId]);

const getRotationStepTranslationSlugCandidates = (step: CropRotationStep): string[] =>
  uniqueStrings([
    ...(step.cropIdOptions ?? []).map((option) => option.translationSlug),
    step.exampleCropTranslationSlug,
    ...(step.cropTranslationSlugOptions ?? []).map((option) => option.translationSlug),
  ]);

const getReferenceEntryForRotationStep = (
  step: CropRotationStep,
  stepIndex: number,
  cycleLength: number,
  entries: FieldCropYearEntry[]
): FieldCropYearEntry | undefined => {
  const cropIdCandidates = new Set(getRotationStepCropIdCandidates(step));
  const translationSlugCandidates = new Set(getRotationStepTranslationSlugCandidates(step));
  const indexedEntries = entries.map((entry, index) => ({ entry, index }));
  const matchesStep = ({ entry }: { entry: FieldCropYearEntry }) =>
    cropIdCandidates.has(entry.mainCrop.cropId) || translationSlugCandidates.has(entry.mainCrop.translationSlug);

  return (
    [...indexedEntries].reverse().find((entry) => entry.index % cycleLength === stepIndex && matchesStep(entry))?.entry ??
    [...indexedEntries].reverse().find(matchesStep)?.entry
  );
};

const resolveRotationStepMainCropId = (step: CropRotationStep, referenceCropId: string | undefined, mainCropOptions: ScenarioMainCropOption[]): string => {
  const candidateIds = uniqueStrings([...getRotationStepCropIdCandidates(step), referenceCropId]);

  for (const candidateId of candidateIds) {
    const resolved = resolveSoilSightMainCropId(candidateId, mainCropOptions);
    if (resolved) return resolved;
  }

  return "";
};

const mapEntryToCropYear = (entry: FieldCropYearEntry, index: number, practices: PracticeFCPO[], mainCropOptions: ScenarioMainCropOption[]): CropYear => {
  const { cropId } = entry.mainCrop;
  const crop = resolveSoilSightMainCropId(cropId, mainCropOptions);
  // Crop was detected but the ID is not in the Silver-model list → leave empty and flag it.
  const hasUnresolvableCrop = !crop && !!cropId?.trim() && mainCropOptions.length > 0;

  return {
    id: `field-import-y-${index + 1}`,
    crop,
    ...(hasUnresolvableCrop && { hasUnresolvableCrop }),
    tillageId: getTillageId(entry.mainCrop.id, practices),
    residue: entry.mainCrop.hasResidue === true,
    coverCropId: entry.coverCrop ? getCoverCropId(entry.coverCrop.cropId) : COVER_CROP_MIX_TYPE.NONE,
  };
};

export const mapFieldCropsToSoilSightYears = (
  fieldCrops: CropFCPO[],
  practices: PracticeFCPO[] = [],
  mainCropOptions: ScenarioMainCropOption[] = []
): CropYear[] => {
  // No main crops resolved (empty field or cover-crop-only) maps to [], which pads to a MIN_YEARS
  // skeleton — the caller gets an incomplete scenario to fill in rather than skipping the import.
  const cropYears: CropYear[] = getFieldCropYearEntries(fieldCrops)
    .slice(0, SCENARIO_LIMITS.MAX_YEARS)
    .map((entry, index) => mapEntryToCropYear(entry, index, practices, mainCropOptions));

  return padCropYearsToMinimum(cropYears);
};

export const mapCropRotationToSoilSightYears = (
  rotation: CropRotationResult,
  fieldCrops: CropFCPO[],
  practices: PracticeFCPO[] = [],
  mainCropOptions: ScenarioMainCropOption[] = []
): CropYear[] => {
  const analysis = getActiveRotationAnalysis(rotation);
  if (analysis.irregular || analysis.label === "insufficient_data" || analysis.label === "perennial" || analysis.pattern.length === 0) return [];

  const entries = getFieldCropYearEntries(fieldCrops);
  const cycleLength = analysis.pattern.length;
  const baseYears: CropYear[] = analysis.pattern.map((step, index) => {
    const referenceEntry = getReferenceEntryForRotationStep(step, index, cycleLength, entries);
    const crop = resolveRotationStepMainCropId(step, referenceEntry?.mainCrop.cropId, mainCropOptions);
    // A crop was in the rotation pattern but none of its candidate IDs are Silver-model crops.
    const stepHasCandidates = getRotationStepCropIdCandidates(step).length > 0 || !!referenceEntry?.mainCrop.cropId;
    const hasUnresolvableCrop = !crop && stepHasCandidates && mainCropOptions.length > 0;

    return {
      id: `field-import-y-${index + 1}`,
      crop,
      ...(hasUnresolvableCrop && { hasUnresolvableCrop }),
      // Null means "unknown / leave unselected" — only set a value when we have real data.
      tillageId: referenceEntry ? getTillageIdOrNull(referenceEntry.mainCrop.id, practices) : null,
      residue: referenceEntry?.mainCrop.hasResidue ?? null,
      // "Not provided" (no reference entry or no cover crop on the entry) → NONE UUID.
      coverCropId: referenceEntry?.coverCrop ? getCoverCropId(referenceEntry.coverCrop.cropId) : COVER_CROP_MIX_TYPE.NONE,
    };
  });

  if (!baseYears.some((year) => year.crop.trim())) return [];

  const expandedStepCount = getExpandedStepCount(baseYears.length);
  const cropYears: CropYear[] = Array.from({ length: expandedStepCount }, (_, index) => ({
    ...baseYears[index % baseYears.length],
    id: `field-import-y-${index + 1}`,
  }));

  return padCropYearsToMinimum(cropYears);
};
